import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { DefaultPathEnum } from '../../../../main/constants/default-path.ts';
import { TrackingEventEnum } from '../../../../main/constants/tracking-event.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { getAwsAuthUtil } from '../../../../main/utils/aws/aws-auth.util.ts';
import { getLocaleUtil } from '../../../../main/utils/locale.util.ts';
import { getToasterUtil } from '../../../../main/utils/toaster.util.ts';
import { getTrackerUtil } from '../../../../main/utils/trackers/tracker.util.ts';
import { getUrlUtil } from '../../../../main/utils/url.util.ts';

import { AUTH_SLICE_NAME, AuthSliceState } from '../auth.slice.types.ts';

interface Props {
  email: string;
  code: string;
}

interface ReturnType {
  isValid: boolean;
}

export const validatePasswordResetCodeThunk = createAsyncThunk<ReturnType, Props, ThunkApiConfigType>(
  `${AUTH_SLICE_NAME}/validatePasswordResetCode`,
  async (props) => {
    const { email, code } = props;
    let isValid = true;

    try {
      // Use an invalid password(< 8 characters) to test the code validity without resetting the password
      // See https://github.com/aws-amplify/amplify-js/issues/4876#issuecomment-2165982362
      const INVALID_PASSWORD = 'invalid';
      await getAwsAuthUtil().confirmForgotPassword(email, code, INVALID_PASSWORD);
    } catch (error: unknown) {
      const { name, message } = error as Error;

      switch (name) {
        case 'InvalidPasswordException':
          isValid = true;
          break;
        case 'CodeMismatchException':
          getToasterUtil().error(getLocaleUtil().select('auth:invalid_verification_code'));
          isValid = false;
          break;
        case 'LimitExceededException':
          getTrackerUtil().track({
            event: TrackingEventEnum.AUTH_ERROR,
            data: {
              email,
              note: name,
            },
          });
          getToasterUtil().error(message);
          isValid = false;
          break;
        default:
          getToasterUtil().error(message ?? getLocaleUtil().select('auth:reset_password_failed'));
          isValid = false;
          break;
      }
    } finally {
      getTrackerUtil().track({
        event: TrackingEventEnum.USER_FORGOT_PASSWORD_CODE_ENTERED,
      });
    }

    if (isValid) {
      getUrlUtil().navigateToUrl(DefaultPathEnum.AUTH_FORGOT_PASSWORD_RESET, true);
    }

    return { isValid };
  }
);

export const validatePasswordResetCodeThunkExtraReducers = (builder: ActionReducerMapBuilder<AuthSliceState>): void => {
  builder.addCase(validatePasswordResetCodeThunk.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(validatePasswordResetCodeThunk.fulfilled, (state) => {
    state.isLoading = false;
  });

  builder.addCase(validatePasswordResetCodeThunk.rejected, (state) => {
    state.isLoading = false;
  });
};
